import { Link, useParams } from "react-router-dom";
import SEO from "../../components/SEO";
import PageHeader from "../../components/PageHeader";
import Breadcrumbs, {
  buildBreadcrumbSchema,
} from "../../components/Breadcrumbs";
import CTASection from "../../components/CTASection";
import SoftwareCard from "../../components/directory/SoftwareCard";
import DirectorySourceList from "../../components/directory/DirectorySourceList";
import OfficialLinkButton from "../../components/directory/OfficialLinkButton";
import LastVerifiedDate from "../../components/directory/LastVerifiedDate";
import DirectoryHubLinks from "../../components/directory/DirectoryHubLinks";
import {
  companies,
  software,
  softwareBySlug,
  softwareCategoryBySlug,
  softwarePath,
} from "../../data/directories";
import { getDirectorySeo } from "../../data/directories/directorySeo";
import { seoConfig } from "../../data/seoConfig";
import {
  faqSchema,
  itemListSchema,
  softwareApplicationSchema,
} from "../../utils/directorySchemas";
import NotFound from "../NotFound";


function SimulationDecisionGuide() {
  const tools = [
    ["Gazebo", "ROS 2-centered robot and sensor simulation", "ROS 2 bridges, physics engine/configuration, sensors, headless workflow, supported Gazebo/ROS pairing"],
    ["NVIDIA Isaac Sim", "GPU-based robotics simulation, perception, synthetic data, Isaac Lab workflows", "GPU/driver requirements, RTX and physics sensors, ROS 2 bridge, asset pipeline, headless deployment"],
    ["MuJoCo", "Articulated-body dynamics, control and reinforcement-learning research", "Contact model, model format, renderer needs, batching/workflow integration, robot-stack bridge"],
    ["Webots", "Cross-platform robot modeling, controllers, education and research", "Controller language, robot models, ROS 2 integration, sensor support, export/deployment workflow"],
    ["CoppeliaSim", "General-purpose scene simulation and scripted robot workflows", "Remote APIs, dynamics backend, ROS 2 integration, scene architecture, licensing for deployment use"],
    ["PyBullet", "Lightweight Python physics experiments and prototyping", "Contact fidelity needed for the task, project maintenance, asset support, determinism and integration work"],
    ["CARLA", "Autonomous-driving environments, traffic and vehicle sensors", "Map/traffic needs, sensor suite, GPU requirements, ROS integration, domain fit; not a general manipulator simulator"],
  ];
  const checklist = [
    ["Mass & inertia", "Use measured or CAD-derived link mass, center of mass, and inertia; default values can make a stable controller look better than the real robot."],
    ["Joints & actuators", "Match joint limits, friction, damping, velocity, torque/force saturation, gearing, controller rate, and command delay."],
    ["Contacts", "Calibrate friction, restitution, compliance, gripper contact, collision geometry, and solver settings against physical tests."],
    ["Sensors", "Match field of view, update rate, resolution, noise, latency, clipping, blind spots, and mounting transforms."],
    ["Timing", "Run the same control and perception rates used on hardware; expose dropped frames, network delay, and asynchronous sensors."],
    ["Validation", "Replay the same test cases in simulation and on hardware, then randomize only parameters that represent plausible physical variation."],
  ];
  return (
    <div className="mt-8 grid gap-6">
      <section className="soft-card overflow-hidden p-5 sm:p-7" aria-labelledby="simulation-guide-title">
        <p className="text-xs font-black uppercase tracking-[0.18em] text-blue-700">Simulation decision matrix</p>
        <h2 id="simulation-guide-title" className="mt-2 text-2xl font-black tracking-tight text-neutral-950">Pick a simulator from the experiment you need to run</h2>
        <p className="mt-4 max-w-4xl text-base leading-8 text-neutral-700">No simulator is "most realistic" for every robot. A navigation team, a contact-rich manipulation team, and an autonomous-driving team need different physics, sensors, assets, compute, and integration. Verify the current official documentation for the version you plan to deploy.</p>
        <div className="mt-6 overflow-x-auto rounded-2xl border border-neutral-200"><table className="min-w-[980px] w-full border-collapse text-left text-sm leading-6"><caption className="sr-only">Robot simulation software comparison</caption><thead className="bg-neutral-950 text-white"><tr><th className="px-4 py-3">Tool</th><th className="px-4 py-3">Strong fit</th><th className="px-4 py-3">Verify before committing</th></tr></thead><tbody className="divide-y divide-neutral-200 bg-white">{tools.map(([tool, fit, verify]) => <tr key={tool}><th className="px-4 py-4 align-top font-black text-neutral-950">{tool}</th><td className="px-4 py-4 align-top text-neutral-700">{fit}</td><td className="px-4 py-4 align-top text-neutral-700">{verify}</td></tr>)}</tbody></table></div>
      </section>
      <section className="soft-card p-5 sm:p-7"><h2 className="text-2xl font-black tracking-tight text-neutral-950">Sim-to-real checklist calibrate what can break the controller</h2><p className="mt-4 max-w-4xl text-base leading-8 text-neutral-700">Treat simulation as a test instrument. Record the parameters that materially change success, compare them against hardware, and keep the mismatch visible instead of hiding it behind prettier rendering.</p><div className="mt-5 grid gap-3 md:grid-cols-2">{checklist.map(([label, value]) => <div key={label} className="rounded-2xl border border-neutral-200 bg-neutral-50 p-4"><p className="font-black text-neutral-950">{label}</p><p className="mt-2 text-sm leading-7 text-neutral-700">{value}</p></div>)}</div></section>
    </div>
  );
}

const categoryComparisonNotes = {
  "robot-operating-systems": "Decide which computer runs each node and which device owns the motor loop. Compare message delivery, timestamps, supported distributions and reconnection behavior. An MCU bridge, industrial driver collection and general middleware serve different parts of that design; check the exact board or controller before choosing.",
  "simulation": "Choose a simulator using a repeatable experiment: a navigation route, a contact task or a driving scenario. Record the physics settings, sensor model, time step and required compute. Compare the result with hardware measurements before treating a simulated success as a deployment result.",
  "robot-offline-programming": "Start with the installed robot controller, tooling and production process. Check the supported postprocessor or virtual controller, then inspect the generated program against the real cell frames. Include fixture geometry, calibration and queues when evaluating cycle time; an animated path is only one part of commissioning.",
  "robot-motion-planning": "Separate arm manipulation, mobile navigation and constrained trajectory optimization. Define the robot model, obstacles, actuator limits and execution interface before comparing planners. Measure planning time and execution failures on the same task; a feasible path and a reliably executed motion are different outcomes.",
  "robot-perception": "Identify whether the required output is an image detection, geometric transform, point cloud or depth estimate. Use recorded sensor data with known calibration and timestamps. Compare error and processing delay under the intended lighting and compute load, then verify how the output reaches the robot coordinate frame.",
  "slam-software": "Choose the sensor input and whether the robot must build a map, localize in an existing map or update it over time. Replay the same route with known reference positions. Inspect loop closures, drift, restart localization and memory use, including corridors or scenes that look alike.",
  "robotics-ai": "Define observations, actions, reset conditions and the task success measure before choosing a learning framework. Check the training environment, data rights and export path to the target robot. Keep held-out tasks and physical trials separate from the training reward; this category currently contains one recorded framework, not a market-wide ranking.",
  "robot-data-platforms": "Separate inspection of recorded robot runs from preparation of datasets for learning. Compare sensor schemas, timestamp alignment, storage, replay and export on one representative recording. For training data, preserve action definitions and dataset versions; for debugging, confirm that an observed failure can be traced back to the relevant messages."
};

function CategoryPage({ category }) {
  const entries = software.filter(
    (item) =>
      item.category === category.name || item.categories.includes(category.name),
  );
  const path = `/robotics-software/${category.slug}`;
  const breadcrumbs = [
    { label: "Home", to: "/" },
    { label: "Robotics Software", to: "/robotics-software" },
    { label: category.name, to: path },
  ];
  const pageSeo = getDirectorySeo(path);
  const schema = itemListSchema({
    name: category.title,
    description: pageSeo.description,
    path,
    items: entries,
    type: "SoftwareApplication",
    getPath: softwarePath,
  });

  return (
    <>
      <SEO
        title={pageSeo.title}
        description={pageSeo.description}
        path={path}
        schema={[buildBreadcrumbSchema(breadcrumbs), schema]}
      />
      <Breadcrumbs items={breadcrumbs} />
      <PageHeader
        eyebrow="Software category"
        title={category.title}
        description={category.description}
      />
      <section className="site-shell py-12 sm:py-16">
        <div className="soft-card p-5 sm:p-7">
          <h2 className="text-2xl font-black text-neutral-950">
            How to compare tools in this category
          </h2>
          <p className="mt-4 text-base leading-8 text-neutral-700">
            {categoryComparisonNotes[category.slug]}
          </p>
        </div>
        {category.slug === "simulation" && <SimulationDecisionGuide />}
        <div className="mt-8 grid gap-5 md:grid-cols-2 xl:grid-cols-3">
          {entries.map((item) => (
            <SoftwareCard key={item.id} software={item} />
          ))}
        </div>
        <div className="mt-8">
          <Link to="/robotics-software" className="btn-secondary">
            Back to the software directory
          </Link>
        </div>
      </section>
      <DirectoryHubLinks exclude="/robotics-software" />
      <CTASection />
    </>
  );
}

function EditorialTextSection({ title, paragraphs = [] }) {
  if (!paragraphs.length) return null;

  return (
    <section className="soft-card p-5 sm:p-8">
      <h2 className="text-2xl font-black tracking-tight text-neutral-950 sm:text-3xl">
        {title}
      </h2>
      <div className="mt-4 grid gap-4 text-base leading-8 text-neutral-700">
        {paragraphs.map((paragraph, index) => (
          <p key={`${title}-${index}`}>{paragraph}</p>
        ))}
      </div>
    </section>
  );
}

function EditorialListSection({ title, items = [] }) {
  if (!items.length) return null;

  return (
    <section className="soft-card p-5 sm:p-8">
      <h2 className="text-2xl font-black tracking-tight text-neutral-950 sm:text-3xl">
        {title}
      </h2>
      <ul className="mt-5 grid gap-3 text-sm leading-7 text-neutral-700 sm:grid-cols-2">
        {items.map((item, index) => (
          <li
            key={`${title}-${index}`}
            className="rounded-2xl border border-neutral-200 bg-neutral-50 px-4 py-3"
          >
            {item}
          </li>
        ))}
      </ul>
    </section>
  );
}

function EditorialFaq({ items = [] }) {
  if (!items.length) return null;

  return (
    <section className="soft-card p-5 sm:p-8">
      <h2 className="text-2xl font-black tracking-tight text-neutral-950 sm:text-3xl">
        Frequently asked questions
      </h2>
      <div className="mt-5 grid gap-4">
        {items.map((item) => (
          <details
            key={item.question}
            className="group rounded-2xl border border-neutral-200 bg-neutral-50 px-4 py-3 open:bg-white"
          >
            <summary className="cursor-pointer list-none pr-6 font-black leading-7 text-neutral-950">
              {item.question}
            </summary>
            <p className="mt-3 text-sm leading-7 text-neutral-700">
              {item.answer}
            </p>
          </details>
        ))}
      </div>
    </section>
  );
}

function RelatedGuideLinks({ items = [] }) {
  if (!items.length) return null;

  return (
    <section className="soft-card p-5 sm:p-8">
      <h2 className="text-2xl font-black tracking-tight text-neutral-950">
        Related robotics guides
      </h2>
      <div className="mt-5 flex flex-wrap gap-3">
        {items.map((item) => (
          <Link
            key={item.to}
            to={item.to}
            className="focus-ring rounded-full border border-neutral-200 px-4 py-2 text-sm font-black text-neutral-800 hover:border-blue-300 hover:text-blue-700"
          >
            {item.label}
          </Link>
        ))}
      </div>
    </section>
  );
}

function SoftwareDetail({ item }) {
  const path = `/robotics-software/${item.slug}`;
  const categorySlug = Object.values(softwareCategoryBySlug).find(
    (category) => category.name === item.category,
  )?.slug;
  const breadcrumbs = [
    { label: "Home", to: "/" },
    { label: "Robotics Software", to: "/robotics-software" },
    ...(categorySlug
      ? [
          {
            label: item.category,
            to: `/robotics-software/${categorySlug}`,
          },
        ]
      : []),
    { label: item.name, to: path },
  ];
  const related = software
    .filter(
      (candidate) =>
        candidate.id !== item.id &&
        (candidate.category === item.category ||
          candidate.rosSupport.some((value) =>
            item.rosSupport.includes(value),
          )),
    )
    .sort((a, b) => Number(b.category === item.category) - Number(a.category === item.category))
    .slice(0, 3);
  const relatedCompanies = companies
    .filter(
      (company) =>
        company.name.toLowerCase() === String(item.developer || "").toLowerCase() ||
        company.products.some((product) =>
          item.name.toLowerCase() === product.toLowerCase(),
        ),
    )
    .slice(0, 4);
  const pageSeo = getDirectorySeo(path);
  const description = pageSeo.description;
  const editorial = item.editorial || {};
  const canonical = `${seoConfig.baseUrl}${path}`;
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "TechArticle",
    headline: pageSeo.title,
    description,
    url: canonical,
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": canonical,
    },
    author: {
      "@type": "Organization",
      name: "TechniaHQRobot",
      url: `${seoConfig.baseUrl}/about-techniahq`,
    },
    publisher: {
      "@type": "Organization",
      name: "TechniaHQRobot",
      url: `${seoConfig.baseUrl}/`,
    },
    dateModified: item.lastVerified || undefined,
    about: {
      "@type": "SoftwareApplication",
      name: item.name,
      url: item.website,
      applicationCategory: "DeveloperApplication",
    },
    keywords: [
      item.name,
      item.category,
      ...item.languages,
      ...item.operatingSystems,
    ].join(", "),
  };
  const schemas = [
    buildBreadcrumbSchema(breadcrumbs),
    softwareApplicationSchema(item),
    articleSchema,
    editorial.faqs?.length ? faqSchema(editorial.faqs) : null,
  ].filter(Boolean);

  return (
    <>
      <SEO
        title={pageSeo.title}
        description={description}
        path={path}
        type="article"
        modifiedTime={item.lastVerified}
        author="TechniaHQRobot"
        section="Robotics software"
        tags={[item.name, item.category, ...item.languages]}
        schema={schemas}
      />
      <Breadcrumbs items={breadcrumbs} />
      <PageHeader
        eyebrow={item.category}
        title={item.name}
        description={item.description}
      />
      <article className="site-shell py-12 sm:py-16">
        <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_20rem]">
          <div className="grid gap-6">
            {editorial.directAnswer ? <section className="soft-card p-5 sm:p-8">
              <p className="text-xs font-black uppercase tracking-[0.2em] text-blue-700">
                Direct answer
              </p>
              <h2 className="mt-3 text-3xl font-black text-neutral-950">
                What {item.name} is used for in robotics
              </h2>
              <p className="mt-4 text-base leading-8 text-neutral-700">
                {editorial.directAnswer}
              </p>
              <div className="mt-5">
                <LastVerifiedDate date={item.lastVerified} />
              </div>
            </section> : <LastVerifiedDate date={item.lastVerified} />}

            <EditorialTextSection
              title={`${item.name} overview`}
              paragraphs={editorial.overview}
            />
            <EditorialListSection
              title="Core capabilities"
              items={editorial.coreCapabilities}
            />
            <EditorialListSection
              title="How the software fits into a robot stack"
              items={editorial.architecture}
            />
            <EditorialListSection
              title="Recommended engineering workflow"
              items={editorial.workflow}
            />
            <EditorialTextSection
              title={`A practical check for ${item.name}`}
              paragraphs={editorial.evaluationNotes}
            />

            <section className="soft-card p-5 sm:p-8">
              <h2 className="text-3xl font-black text-neutral-950">
                Compatibility and installation
              </h2>
              <dl className="mt-5 grid gap-5 text-sm leading-7 sm:grid-cols-2">
                <div>
                  <dt className="font-black">Developer</dt>
                  <dd>{item.developer}</dd>
                </div>
                <div>
                  <dt className="font-black">License</dt>
                  <dd>{item.license}</dd>
                </div>
                <div>
                  <dt className="font-black">Operating systems</dt>
                  <dd>{item.operatingSystems.join(", ")}</dd>
                </div>
                <div>
                  <dt className="font-black">ROS support</dt>
                  <dd>
                    {item.rosSupport.length
                      ? item.rosSupport.join(", ")
                      : "No official ROS support recorded"}
                  </dd>
                </div>
                <div>
                  <dt className="font-black">Languages</dt>
                  <dd>{item.languages.join(", ")}</dd>
                </div>
                <div>
                  <dt className="font-black">Installation method</dt>
                  <dd>{item.installationMethod}</dd>
                </div>
              </dl>
            </section>

            <EditorialListSection
              title="Installation and deployment notes"
              items={editorial.installationNotes}
            />
            <EditorialListSection
              title="Robotics use cases"
              items={editorial.useCases}
            />
            <EditorialListSection
              title="Limitations and engineering cautions"
              items={editorial.limitations}
            />
            <EditorialFaq items={editorial.faqs} />
            <RelatedGuideLinks items={editorial.relatedGuides} />

            <section className="soft-card p-5 sm:p-8">
              <h2 className="text-3xl font-black text-neutral-950">
                Use official installation sources
              </h2>
              <p className="mt-4 text-base leading-8 text-neutral-700">
                Use the vendor download page, official documentation, package
                repository or official GitHub release. Third-party installers
                and mirrors are not linked from this directory.
              </p>
            </section>
            <DirectorySourceList sources={item.sources} />
          </div>

          <aside className="grid content-start gap-5">
            <section className="soft-card p-5">
              <h2 className="text-xl font-black text-neutral-950">
                Official links
              </h2>
              <div className="mt-4 grid gap-3">
                <OfficialLinkButton href={item.website} variant="primary">
                  Visit official website
                </OfficialLinkButton>
                <OfficialLinkButton href={item.downloadUrl}>
                  Download from official source
                </OfficialLinkButton>
                <OfficialLinkButton href={item.documentationUrl}>
                  View documentation
                </OfficialLinkButton>
                <OfficialLinkButton href={item.githubUrl}>
                  View on GitHub
                </OfficialLinkButton>
              </div>
            </section>

            {related.length > 0 && (
              <section className="soft-card p-5">
                <h2 className="text-xl font-black text-neutral-950">
                  Related software
                </h2>
                <div className="mt-4 grid gap-2">
                  {related.map((candidate) => (
                    <Link
                      key={candidate.id}
                      to={`/robotics-software/${candidate.slug}`}
                      className="focus-ring rounded-xl border border-neutral-200 px-3 py-2 text-sm font-bold hover:border-blue-300 hover:text-blue-700"
                    >
                      {candidate.name}
                    </Link>
                  ))}
                </div>
              </section>
            )}

            {relatedCompanies.length > 0 && (
              <section className="soft-card p-5">
                <h2 className="text-xl font-black text-neutral-950">
                  Developer and listed products
                </h2>
                <div className="mt-4 grid gap-2">
                  {relatedCompanies.map((company) => (
                    <Link
                      key={company.id}
                      to={`/robotics-companies/${company.slug}`}
                      className="text-sm font-bold text-blue-700"
                    >
                      {company.name}
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </aside>
        </div>
      </article>
      <DirectoryHubLinks />
      <CTASection />
    </>
  );
}

export default function RoboticsSoftwareRoutePage() {
  const { slug } = useParams();
  if (softwareCategoryBySlug[slug]) {
    return <CategoryPage category={softwareCategoryBySlug[slug]} />;
  }
  if (softwareBySlug[slug]) return <SoftwareDetail item={softwareBySlug[slug]} />;
  return <NotFound />;
}
